import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import CustomStore from "devextreme/data/custom_store";
import DGrid, {
  Column,
  Pager,
  Paging,
  HeaderFilter,
  FilterRow,
  FilterPanel,
  FilterBuilderPopup,
  Button,
  ColumnChooser,
} from "devextreme-react/data-grid";
import { confirm } from "devextreme/ui/dialog";
import ButtonGroup from "./button-group";

import BaseService from "../../services";
import Modal from "../modal";
import "./style.scss";
import { DataGridButtonProps, GridButtonType } from "./types";
import { getApiParams } from "../../redux/auth";
import { BodyType } from "../../redux/base/request";
import { getLocalized } from "../../redux/localization";

const filterBuilderPopupPosition = {
  of: window,
  at: "top",
  my: "top",
  offset: { y: 10 },
};
const visible = { visible: true };
const allowedPageSizes = [10, 15, 20, "all"];

type DataGridProps = {
  gridButtons?: GridButtonType[];
  buttons?: DataGridButtonProps[];
  key?: string;
  sort?: boolean;
  url: string | undefined;
  getByIdUrl: string;
  columns: any[];
  refObject: any;
  cSize?: any;
  create?: {
    width?: string;
    isJson?: boolean;
    lookup?: {
      lookupUrl?: string;
      periodUrl?: string;
      kmLimitUrl?: string;
    };
    component: any;
    createUrl: string;
    title: string;
    height?: string;
    validationGroup?: string;
  };
  edit?: {
    lookup?: {
      lookupUrl?: string;
      periodUrl?: string;
      kmLimitUrl?: string;
    };

    width?: string;
    component: any;
    editUrl: string;
    title: string;
    height?: string;
    validationGroup?: string;
  };
  remove?: {
    url?: string;
  };
  details?: {
    width?: string;
    component: any;
    getByIdUrl: string;
    title: string;
    height?: string;
  };
  title?: string;
};

const DataGrid = ({
  gridButtons,
  key,
  url,
  columns,
  refObject,
  create,
  edit,
  remove,
  details,
  getByIdUrl,
  buttons,
  title,
  sort,
  cSize,
}: DataGridProps) => {
  const strings = useSelector(getLocalized);
  const baseService = new BaseService();
  const tableRef = useRef<any>();
  const formRef = useRef<any>();
  const data = useRef<any>(refObject);
  const lookupData = useRef<any>();
  const editLookupData = useRef<any>();
  const periodLookupData = useRef<any>();
  const kmLimitLookupData = useRef<any>();

  const [createVisible, setCreateVisible] = useState<boolean>(false);
  const [editVisible, setEditVisible] = useState<undefined | string>();
  const [detailsVisible, setDetailsVisible] = useState<undefined | string>();
  const customStore = new CustomStore({
    key: key || "Id",
    //@ts-ignore
    load: () => baseService.sendRequest(url, "GET", token, "tr-TR"),
  });
  const { token, lang } = useSelector(getApiParams);

  const onModalClose = (type: "create" | "edit" | "details" = "edit") => {
    if (formRef.current) {
      formRef.current._instance.resetValues();
    }
    if (type === "create") {
      setCreateVisible(false);
    } else if (type === "edit") {
      setEditVisible(undefined);
    } else {
      setDetailsVisible(undefined);
    }
  };

  /** GET DETAIL REQUEST **/
  const onGetDetailClicked = async (
    Id: string,
    type: "edit" | "details" = "edit"
  ) => {
    const response: any | null = await baseService.sendRequestWithApiResponse(
      getByIdUrl + "/" + Id,
      "GET",
      token,
      lang,
      BodyType.formdata
    );
    data.current = response.Result;
    data.current.Translates = response.Translates;
    if (response) {
      if (type === "edit") {
        if (edit?.lookup?.lookupUrl) {
          const response: any = await baseService.sendRequest(
            edit?.lookup?.lookupUrl,
            "GET",
            token,
            lang,
            BodyType.formdata
          );

          editLookupData.current = response;
        }
        if (edit?.lookup?.periodUrl) {
          const response: any = await baseService.sendRequest(
            edit?.lookup?.periodUrl,
            "GET",
            token,
            lang,
            BodyType.formdata
          );
          periodLookupData.current = response;
        }
        if (edit?.lookup?.kmLimitUrl) {
          const response: any = await baseService.sendRequest(
            edit?.lookup?.kmLimitUrl,
            "GET",
            token,
            lang,
            BodyType.formdata
          );
          kmLimitLookupData.current = response;
        }
        setEditVisible(Id);
      } else {
        setDetailsVisible(Id);
      }
    }
  };

  /** ADD REQUEST **/
  const onAddSuccess = async (data: any) => {
    if (create?.createUrl) {
      await baseService.sendRequest(
        create?.createUrl,
        "POST",
        token,
        lang,
        data,
        //BodyType.raw
        create.isJson && create.isJson ? BodyType.raw : BodyType.formdata
      );
      onModalClose("create");
      tableRef.current.instance.refresh();
    }
  };
  /** EDIT REQUEST **/
  const onEditSuccess = async (data: any) => {
    if (edit?.editUrl) {
      await baseService.sendRequest(
        edit?.editUrl + "/" + data.Id,
        "PUT",
        token,
        lang,
        data,
        BodyType.formdata
      );
      onModalClose();
      tableRef.current.instance.refresh();
    }
  };

  /** DELETE REQUEST **/
  const onDeleteClicked = (Id: string) => {
    let confirmResult = confirm("Are you sure?", "Delete item");
    confirmResult.then(async (dialogResult) => {
      if (dialogResult && remove?.url) {
        await baseService.sendRequest(
          remove.url + "/" + Id,
          "DELETE",
          token,
          lang
        );
        tableRef.current.instance.refresh();
      }
    });
  };

  //Lookup Data
  const onCreateModalClicked = async () => {
    if (create?.lookup?.lookupUrl) {
      const response: any = await baseService.sendRequest(
        create?.lookup?.lookupUrl,
        "GET",
        token,
        lang,
        BodyType.formdata
      );
      lookupData.current = response;
    }
    if (create?.lookup?.periodUrl) {
      const response: any = await baseService.sendRequest(
        create?.lookup?.periodUrl,
        "GET",
        token,
        lang,
        BodyType.formdata
      );

      periodLookupData.current = response;
    }
    if (create?.lookup?.kmLimitUrl) {
      const response: any = await baseService.sendRequest(
        create?.lookup?.kmLimitUrl,
        "GET",
        token,
        lang,
        BodyType.formdata
      );
      kmLimitLookupData.current = response;
    }
    data.current = refObject;
    setCreateVisible(true);
  };

  return (
    <>
      <div
        style={{
          textAlign: "start",
          fontSize: "22px",
          fontWeight: 700,
          padding: "10px 0px 0px 10px",
        }}
      >
        {title}
      </div>
      <ButtonGroup
        gridButtons={gridButtons}
        gridInstance={tableRef?.current?._instance}
        create={create?.component}
        onCreate={onCreateModalClicked}
        refresh
        exportExcel
        columnChooser
        clearFilters
      />
      <DGrid
        ref={tableRef}
        key="Id"
        className={"custom-data-grid wide-card"}
        dataSource={customStore}
        showBorders
        showRowLines
        showColumnLines={false}
        columnAutoWidth
        columnHidingEnabled
        rowAlternationEnabled
        allowColumnResizing
        allowColumnReordering
        focusedRowEnabled
        searchPanel={visible}
        groupPanel={visible}
        grouping={{
          autoExpandAll: false,
        }}
      >
        <Paging defaultPageSize={15} />
        <Pager
          visible
          showPageSizeSelector
          showInfo
          showNavigationButtons
          allowedPageSizes={allowedPageSizes}
        />
        <HeaderFilter visible={true} />
        <FilterRow visible={true} />
        <FilterPanel visible={true} />
        <FilterBuilderPopup position={filterBuilderPopupPosition} />
        <ColumnChooser mode="select" />

        {window.location.hash === "#/productFeatures" ||
        window.location.hash === "#/categories"
          ? columns.map((column, i) =>
              column.dataField ===
              (window.location.hash === "#/productFeatures"
                ? "Code"
                : "ParentCategoryName") ? (
                <Column key={i} {...column} groupIndex={0} />
              ) : (
                <Column key={i} {...column} />
              )
            )
          : columns.map((column, i) =>
              column.dataField === "IsComplated" ? (
                <Column key={i} {...column} defaultSortOrder="asc" />
              ) : (
                <Column key={i} {...column} />
              )
            )}
        {/* {

          window.location.hash === "#/categories" ?
            columns.map((column, i) =>
              column.dataField === "ParentCategoryName" ? <Column key={i} {...column} groupIndex={0} /> : <Column key={i} {...column} />
            )
            :
            columns.map((column, i) =>
              <Column key={i} {...column} />
            )
        } */}
        <Column type="buttons">
          {buttons?.map((button: any) => (
            <Button
              visible={button.visibility}
              hint={button.hint}
              icon={button.icon}
              onClick={button.onClick}
            />
          ))}
          <Button
            visible={details !== undefined}
            icon="info"
            onClick={(e: any) => onGetDetailClicked(e.row.key, "details")}
          />
          <Button
            visible={edit !== undefined}
            icon="edit"
            onClick={(e: any) => onGetDetailClicked(e.row.key)}
          />
          <Button
            visible={remove !== undefined}
            icon="trash"
            onClick={(e: any) => onDeleteClicked(e.row.key)}
          />
        </Column>
      </DGrid>
      {details && (
        <Modal
          width={details.width}
          visible={detailsVisible !== undefined}
          onHiding={() => onModalClose("details")}
          title={details.title}
          height={details.height}
          onSubmitClick={() => onEditSuccess(data.current)}
          showButton={false}
        >
          <details.component data={data.current} />
        </Modal>
      )}
      {edit && (
        <Modal
          width={edit.width}
          visible={editVisible !== undefined}
          onHiding={() => onModalClose("edit")}
          validationGroup={edit.validationGroup}
          title={edit.title}
          height={edit.height}
          buttonTitle={strings.getString("update")}
          onSubmitClick={() => onEditSuccess(data.current)}
        >
          <edit.component
            data={data.current}
            lookup={editLookupData.current}
            periodlookup={periodLookupData.current}
            kmlimitlookup={kmLimitLookupData.current}
          />
        </Modal>
      )}
      {create && (
        <Modal
          width={create.width}
          visible={createVisible}
          onHiding={() => onModalClose("create")}
          validationGroup={create.validationGroup}
          title={create.title}
          height={create.height}
          buttonTitle={strings.getString("add")}
          onSubmitClick={() => onAddSuccess(data.current)}
        >
          <create.component
            ref={formRef}
            data={data.current}
            lookup={lookupData.current}
            periodlookup={periodLookupData.current}
            kmlimitlookup={kmLimitLookupData.current}
          />
        </Modal>
      )}
    </>
  );
};
export default DataGrid;
